import Image from 'next/image';
import ArtistLineup from '../artist-lineup/artist-lineup';
import { TimeBlock } from '../time-block/time-block';
import { CtaButton } from '../cta-button/cta-button';
import React from 'react';

/**
 * TaperedGlowBorder Component
 *
 * Hiển thị một đường kẻ dọc có hiệu ứng phát sáng và được làm thon/vuốt nhọn ở hai đầu.
 * - Hiệu ứng phát sáng được tạo bằng background-image: linear-gradient ngang.
 * - Hình dáng vuốt nhọn được tạo bằng mask-image: linear-gradient dọc.
 */
const TaperedGlowBorder = () => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#333]">
      <div
        className="h-96 w-5 bg-gradient-to-r from-transparent via-white/80 to-transparent"
        style={{
          maskImage: 'linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)',
        }}
      ></div>
    </div>
  );
};

/**
 * SharpGlowBorder Component
 *
 * Hiển thị một đường kẻ dọc có hiệu ứng phát sáng chói ở giữa
 * và hai đầu được vuốt nhọn hoắt.
 */
const SharpGlowBorder = () => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#333] p-10">
      <div
        className="h-[500px] w-8" // Tăng chiều cao và chiều rộng một chút để hiệu ứng rõ hơn
        style={{
          // 1. TẠO HIỆU ỨNG CHÓI SÁNG Ở GIỮA
          // Gradient này tạo một lõi trắng sáng và sắc nét ở chính giữa (từ 49.5% đến 50.5%),
          // bao quanh bởi một quầng sáng xanh nhạt mờ hơn, và sau đó mờ dần ra trong suốt.
          backgroundImage: 'linear-gradient(to right, transparent 40%, rgba(220, 235, 255, 0.2) 48%, white 49.5%, white 50.5%, rgba(220, 235, 255, 0.2) 52%, transparent 60%)',

          // 2. TẠO HÌNH DÁNG VUỐT NHỌN HOẮT
          // Gradient của mask-image này chuyển từ trong suốt sang đen (hiển thị) rất nhanh ở hai đầu (chỉ trong khoảng 5% chiều cao),
          // tạo ra cảm giác đầu nhọn và sắc nét hơn nhiều.
          maskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
        }}
      ></div>
    </div>
  );
};

/**
 * BlueGlowBorder Component
 *
 * Hiển thị một đường kẻ dọc có hiệu ứng phát sáng màu xanh blue,
 * với phần lõi sáng dày hơn và hai đầu được vuốt nhọn.
 */
const BlueGlowBorder = () => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#333] p-10">
      <div
        className="h-[500px] w-8"
        style={{
          // 1. TẠO HIỆU ỨNG ÁNH SÁNG BLUE DÀY HƠN
          // Gradient được điều chỉnh để có lõi sáng màu xanh nhạt (#aaddff) rộng hơn (từ 47% đến 53%).
          // Quầng sáng xung quanh (rgba(0, 170, 255, 0.4)) cũng có màu xanh đậm hơn để tạo chiều sâu.
          backgroundImage: 'linear-gradient(to right, transparent 40%, rgba(0, 170, 255, 0.4) 45%, #aaddff 47%, #aaddff 53%, rgba(0, 170, 255, 0.4) 55%, transparent 60%)',

          // 2. GIỮ NGUYÊN HÌNH DÁNG VUỐT NHỌN
          // Mask-image không đổi để giữ nguyên hiệu ứng hai đầu nhọn hoắt.
          maskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
        }}
      ></div>
    </div>
  );
};
/**
 * WideHaloBorder Component
 *
 * Hiển thị một đường kẻ dọc có hiệu ứng hào quang ở giữa,
 * với phần thân to hơn và hai đầu vẫn vuốt nhọn.
 */
const WideHaloBorder = () => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#333] p-10">
      <div
        // Tăng chiều rộng để có không gian cho hiệu ứng to hơn
        className="h-[500px] w-14"
        style={{
          // 1. TẠO HIỆU ỨNG HÀO QUANG NHIỀU LỚP VÀ DÀY HƠN
          // Gradient này tạo ra 3 lớp ánh sáng:
          // - Lõi trong cùng (#ccf0ff): Sáng nhất, tạo điểm nhấn.
          // - Lớp hào quang chính (rgba(0, 170, 255, 0.5)): Màu xanh đậm hơn, lan tỏa ra xung quanh.
          // - Lớp hào quang ngoài (rgba(0, 170, 255, 0.2)): Mờ nhất, tạo cảm giác ánh sáng mềm mại.
          // Toàn bộ phần phát sáng giờ rộng hơn đáng kể.
          backgroundImage: 'linear-gradient(to right, transparent 35%, rgba(0, 170, 255, 0.2) 40%, rgba(0, 170, 255, 0.5) 44%, #ccf0ff 48%, #ccf0ff 52%, rgba(0, 170, 255, 0.5) 56%, rgba(0, 170, 255, 0.2) 60%, transparent 65%)',

          // 2. GIỮ NGUYÊN HÌNH DÁNG VUỐT NHỌN
          maskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
        }}
      ></div>
    </div>
  );
};

/**
 * FinalGlowBorder Component
 *
 * Hiển thị một đường kẻ dọc có hiệu ứng ánh sáng xanh, thân dày,
 * hai đầu vuốt nhọn, và có một lớp hào quang tỏa ra xung quanh.
 * Hào quang này cũng được vuốt nhọn theo.
 */
const FinalGlowBorder = () => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#333] p-10">
      <div
        className="h-[500px] w-14"
        style={{
          // Ánh sáng bên trong
          backgroundImage: 'linear-gradient(to right, transparent 35%, rgba(0, 170, 255, 0.2) 40%, rgba(0, 170, 255, 0.5) 44%, #ccf0ff 48%, #ccf0ff 52%, rgba(0, 170, 255, 0.5) 56%, rgba(0, 170, 255, 0.2) 60%, transparent 65%)',

          // Cắt hình dáng vuốt nhọn
          maskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 5%, black 95%, transparent)',

          // HÀO QUANG VUỐT NHỌN THEO
          // `drop-shadow` sẽ tự động tạo bóng đổ theo hình dáng đã bị cắt bởi `maskImage`.
          // Vì vậy, hào quang này cũng sẽ có hai đầu nhọn y hệt như thanh kiếm.
          filter: `
            drop-shadow(0 0 10px rgba(0, 170, 255, 0.9)) 
            drop-shadow(0 0 30px rgba(0, 170, 255, 0.6))
          `
        }}
      ></div>
    </div>
  );
};

const SuperHeroBanner = () => {
  const basePath = 'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/supper-banner';
  return (
    <div className="isolate relative max-w-[2000px] mx-auto
     w-full max-h-[1862px] lg:max-h-none xl:max-h-[1862px]
     aspect-[1/3.7]
     xs:aspect-[1/3]
     sm:aspect-[1/2.5]
     md:aspect-[1/2.15]
     lg:aspect-[1/1.1]
     xl:aspect-[1/1.1]
     2xl:aspect-[1/1]
     3xl:aspect-[2000/1862]
     overflow-hidden
     super-hero-banner"
		// style={{ backgroundColor: '#060BDB' }}
    >

      <div className="absolute inset-0">
        <Image
          src={`${basePath}/background.png`}
          alt="Background"
          fill
          layout="fill" objectFit="cover"
        />
      </div>

      {/* Partner Logos */}
      <div className="absolute
        top-[0.27%] left-[17%]
        w-[13.8%] h-[8.75%]
        xxs:top-[0.27%] xxs:left-[17%] xxs:w-[13.8%] xxs:h-[8.75%]
        xs:top-[0.27%] xs:left-[17%] xs:w-[13.8%] xs:h-[8.75%]
        sm:top-[0.27%] sm:left-[17%] sm:w-[13.8%] sm:h-[8.75%]
        md:top-[0.27%] md:left-[17%] md:w-[13.8%] md:h-[8.75%]
        lg:top-[0.27%] lg:left-[17%] lg:w-[13.8%] lg:h-[8.75%]
        xl:top-[0.27%] xl:left-[17%] xl:w-[13.8%] xl:h-[8.75%]">
        <Image
          src={`${basePath}/kidsplaza-logo-vuong-trai.png`}
          alt="KidsPlaza Logo"
          fill
          className="object-contain object-top"
        />
      </div>
      <div className="absolute
        top-[0.27%] right-[17%]
        w-[11.35%] h-[7.14%]
        xxs:top-[0.27%] xxs:right-[17%] xxs:w-[11.35%] xxs:h-[7.14%]
        xs:top-[0.27%] xs:right-[17%] xs:w-[11.35%] xs:h-[7.14%]
        sm:top-[0.27%] sm:right-[17%] sm:w-[11.35%] sm:h-[7.14%]
        md:top-[0.27%] md:right-[17%] md:w-[11.35%] md:h-[7.14%]
        lg:top-[0.27%] lg:right-[17%] lg:w-[11.35%] lg:h-[7.14%]
        xl:top-[0.27%] xl:right-[17%] xl:w-[11.35%] xl:h-[7.14%]">
        <Image
          src={`${basePath}/festival-logo-vuong-phai.png`}
          alt="Festival Logo"
          fill
          className="object-contain object-top"
        />
      </div>


      {/* Ellipse Decorations - Overlay blend mode */}
      {/*<div className="absolute top-20 left-20 ">*/}
      {/*  <Image*/}
      {/*    src={`${basePath}/ellipse-layer-10.png`}*/}
      {/*    alt="Ellipse Layer 10"*/}
      {/*    fill*/}
      {/*    className="object-contain object-top mix-blend-lighten"*/}
      {/*  />*/}
      {/*</div>*/}
      {/*<div className="absolute top-32 right-32">*/}
      {/*  <Image*/}
      {/*    src={`${basePath}/ellipse-layer-11.png`}*/}
      {/*    alt="Ellipse Layer 11"*/}
      {/*    fill*/}
      {/*    className="object-contain object-top mix-blend-lighten"*/}
      {/*  />*/}
      {/*</div>*/}

      {/* Stage Lighting Effects */}
      <div className="absolute
        top-[0%] left-[-1.52%]
        w-[103.31%] h-[9.44%]
        xxs:top-[0%] xxs:left-[-1.52%] xxs:w-[103.31%] xxs:h-[9.44%]
        xs:top-[0%] xs:left-[-1.52%] xs:w-[103.31%] xs:h-[9.44%]
        sm:top-[0%] sm:left-[-1.52%] sm:w-[103.31%] sm:h-[9.44%]
        md:top-[0%] md:left-[-1.52%] md:w-[103.31%] md:h-[9.44%]
        lg:top-[0%] lg:left-[-1.52%] lg:w-[103.31%] lg:h-[9.44%]
        xl:top-[0%] xl:left-[-1.52%] xl:w-[103.31%] xl:h-[9.44%]">
        <Image
          src={`${basePath}/hinh-chu-nhat-gradient.png`}
          alt="Gradient Rectangle"
          fill
          className="object-contain object-top mix-blend-multiply"
        />
      </div>
      <div className="absolute
        top-[0.58%] left-[calc(50%-14.625%)]
        w-[29.25%] h-[18.9%]
        xs:top-[0.58%] xs:left-[calc(50%-14.625%)] xs:w-[29.25%] xs:h-[18.9%]
        sm:top-[0.58%] sm:left-[calc(50%-14.625%)] sm:w-[29.25%] sm:h-[18.9%]
        md:top-[0.58%] md:left-[calc(50%-14.625%)] md:w-[29.25%] md:h-[18.9%]
        lg:top-[0.58%] lg:left-[calc(50%-14.625%)] lg:w-[29.25%] lg:h-[18.9%]
        xl:top-[0.58%] xl:left-[calc(50%-14.625%)] xl:w-[29.25%] xl:h-[18.9%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/den-san-khau-tren.webp'}
          alt="Stage Light Top"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>
      {/* Phoenix/Bird */}
      <div className="absolute
        top-[1%] left-[22.77%]
        w-[53.06%] h-[34.11%]
        xs:top-[-4.84%] xs:left-[22.77%] xs:w-[53.06%] xs:h-[34.11%]
        sm:top-[-4.84%] sm:left-[22.77%] sm:w-[53.06%] sm:h-[34.11%]
        md:top-[-4.84%] md:left-[22.77%] md:w-[53.06%] md:h-[34.11%]
        lg:top-[-4.84%] lg:left-[22.77%] lg:w-[53.06%] lg:h-[34.11%]
        xl:top-[-4.84%] xl:left-[22.77%] xl:w-[53.06%] xl:h-[34.11%]">
        <Image
          src={`${basePath}/phoenix.png`}
          alt="Phoenix"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>

      <div className="absolute
        top-[1%] left-[1%]
        w-[31.45%] h-[37.54%]
        xs:top-[1%] xs:left-[1%] xs:w-[31.45%] xs:h-[37.54%]
        sm:top-[1%] sm:left-[1%] sm:w-[31.45%] sm:h-[37.54%]
        md:top-[1%] md:left-[1%] md:w-[31.45%] md:h-[37.54%]
        lg:top-[1%] lg:left-[1%] lg:w-[31.45%] lg:h-[37.54%]
        xl:top-[1%] xl:left-[1%] xl:w-[31.45%] xl:h-[37.54%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/den-trai.webp'}
          alt="Left Light"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>
      <div className="absolute
        right-[1%] top-[-8%]
        w-[31.45%] h-[37.54%]
        xs:right-[1%] xs:top-[-8%] xs:w-[31.45%] xs:h-[37.54%]
        sm:right-[1%] sm:top-[-8%] sm:w-[31.45%] sm:h-[37.54%]
        md:right-[1%] md:top-[-8%] md:w-[31.45%] md:h-[37.54%]
        lg:right-[1%] lg:top-[-8%] lg:w-[31.45%] lg:h-[37.54%]
        xl:right-[1%] xl:top-[-8%] xl:w-[31.45%] xl:h-[37.54%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/den-phai.webp'}
          alt="Right Light"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>
      <div className="absolute
        top-[6.71%] left-[13.25%]
        w-[16.35%] h-[18.05%]
        xs:top-[6.71%] xs:left-[13.25%] xs:w-[16.35%] xs:h-[18.05%]
        sm:top-[6.71%] sm:left-[13.25%] sm:w-[16.35%] sm:h-[18.05%]
        md:top-[6.71%] md:left-[13.25%] md:w-[16.35%] md:h-[18.05%]
        lg:top-[6.71%] lg:left-[13.25%] lg:w-[16.35%] lg:h-[18.05%]
        xl:top-[6.71%] xl:left-[13.25%] xl:w-[16.35%] xl:h-[18.05%]">
        <Image
          src={`${basePath}/den-pha-trai.png`}
          alt="Left Spotlight"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>
      <div className="absolute
        top-[6.71%] right-[13.25%]
        w-[16.35%] h-[18.05%]
        xs:top-[6.71%] xs:right-[13.25%] xs:w-[16.35%] xs:h-[18.05%]
        sm:top-[6.71%] sm:right-[13.25%] sm:w-[16.35%] sm:h-[18.05%]
        md:top-[6.71%] md:right-[13.25%] md:w-[16.35%] md:h-[18.05%]
        lg:top-[6.71%] lg:right-[13.25%] lg:w-[16.35%] lg:h-[18.05%]
        xl:top-[6.71%] xl:right-[13.25%] xl:w-[16.35%] xl:h-[18.05%]">
        <Image
          src={`${basePath}/den-pha-phai.png`}
          alt="Right Spotlight"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>

      {/* Character Lighting */}
      <div className="absolute
        top-[4.46%] left-[-14.1%]
        w-[47.3%] h-[57.15%]
        xs:top-[4.46%] xs:left-[-14.1%] xs:w-[47.3%] xs:h-[57.15%]
        sm:top-[4.46%] sm:left-[-14.1%] sm:w-[47.3%] sm:h-[57.15%]
        md:top-[4.46%] md:left-[-14.1%] md:w-[47.3%] md:h-[57.15%]
        lg:top-[4.46%] lg:left-[-14.1%] lg:w-[47.3%] lg:h-[57.15%]
        xl:top-[4.46%] xl:left-[-14.1%] xl:w-[47.3%] xl:h-[57.15%]">
        <Image
          src={`${basePath}/cheo-trai.png`}
          alt="Left CHEO Character"
          fill
          className="object-contain object-top opacity-5"
        />
      </div>

      <div className="absolute
        top-[7.36%] left-[-11.7%]
        w-[48.65%] h-[61.28%]
        xs:top-[7.36%] xs:left-[-11.7%] xs:w-[48.65%] xs:h-[61.28%]
        sm:top-[7.36%] sm:left-[-11.7%] sm:w-[48.65%] sm:h-[61.28%]
        md:top-[7.36%] md:left-[-11.7%] md:w-[48.65%] md:h-[61.28%]
        lg:top-[7.36%] lg:left-[-11.7%] lg:w-[48.65%] lg:h-[61.28%]
        xl:top-[7.36%] xl:left-[-11.7%] xl:w-[48.65%] xl:h-[61.28%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/den-cheo-trai.webp'}
          alt="Left Character Light"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>

      <div className="absolute
        top-[4.46%] right-[-14.8%]
        w-[47.3%] h-[57.15%]
        xs:top-[4.46%] xs:right-[-14.8%] xs:w-[47.3%] xs:h-[57.15%]
        sm:top-[4.46%] sm:right-[-14.8%] sm:w-[47.3%] sm:h-[57.15%]
        md:top-[4.46%] md:right-[-14.8%] md:w-[47.3%] md:h-[57.15%]
        lg:top-[4.46%] lg:right-[-14.8%] lg:w-[47.3%] lg:h-[57.15%]
        xl:top-[4.46%] xl:right-[-14.8%] xl:w-[47.3%] xl:h-[57.15%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/cheo-phai.webp'}
          alt="Right CHEO Character"
          fill
          className="object-contain object-top opacity-5"
        />
      </div>

      <div className="absolute
        top-[7.36%] left-[64.4%]
        w-[48.65%] h-[61.28%]
        xs:top-[7.36%] xs:left-[64.4%] xs:w-[48.65%] xs:h-[61.28%]
        sm:top-[7.36%] sm:left-[64.4%] sm:w-[48.65%] sm:h-[61.28%]
        md:top-[7.36%] md:left-[64.4%] md:w-[48.65%] md:h-[61.28%]
        lg:top-[7.36%] lg:left-[64.4%] lg:w-[48.65%] lg:h-[61.28%]
        xl:top-[7.36%] xl:left-[64.4%] xl:w-[48.65%] xl:h-[61.28%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/den-cheo-phai.webp'}
          alt="Right Character Light"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>

      {/*<div className="absolute*/}
      {/*  top-[12%] left-[calc(50%-11.25%)]*/}
      {/*  w-[27.5%] h-[19.01%]*/}
      {/*  xs:top-[12%] xs:left-[calc(50%-11.25%)] xs:w-[27.5%] xs:h-[19.01%]*/}
      {/*  sm:top-[12%] sm:left-[calc(50%-11.25%)] sm:w-[27.5%] sm:h-[19.01%]*/}
      {/*  md:top-[12%] md:left-[calc(50%-11.25%)] md:w-[27.5%] md:h-[19.01%]*/}
      {/*  lg:top-[12%] lg:left-[calc(50%-11.25%)] lg:w-[27.5%] lg:h-[19.01%]*/}
      {/*  xl:top-[12%] xl:left-[calc(50%-11.25%)] xl:w-[27.5%] xl:h-[19.01%]">*/}
      {/*  <Image*/}
      {/*    src={`${basePath}/ellipse-layer-17.png`}*/}
      {/*    alt="Ellipse Layer 17"*/}
      {/*    fill*/}
      {/*    className="object-contain object-top mix-blend-multiply"*/}
      {/*  />*/}
      {/*</div>*/}

      {/* Main Light Effects */}

      <div className="absolute
        top-[17.89%] left-[calc(50%-31.025%)]
        w-[62.05%] h-[46.51%]
        xs:top-[17.89%] xs:left-[calc(50%-31.025%)] xs:w-[62.05%] xs:h-[46.51%]
        sm:top-[17.89%] sm:left-[calc(50%-31.025%)] sm:w-[62.05%] sm:h-[46.51%]
        md:top-[17.89%] md:left-[calc(50%-31.025%)] md:w-[62.05%] md:h-[46.51%]
        lg:top-[17.89%] lg:left-[calc(50%-31.025%)] lg:w-[62.05%] lg:h-[46.51%]
        xl:top-[17.89%] xl:left-[calc(50%-31.025%)] xl:w-[62.05%] xl:h-[46.51%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/sang-1x.webp'}
          alt="Shine Effect"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>
      {/*<div className="absolute*/}
      {/*  top-[25.03%] left-[26.25%]*/}
      {/*  w-[24.75%] h-[22.56%]*/}
      {/*  xs:top-[25.03%] xs:left-[26.25%] xs:w-[24.75%] xs:h-[22.56%]*/}
      {/*  sm:top-[25.03%] sm:left-[26.25%] sm:w-[24.75%] sm:h-[22.56%]*/}
      {/*  md:top-[25.03%] md:left-[26.25%] md:w-[24.75%] md:h-[22.56%]*/}
      {/*  lg:top-[25.03%] lg:left-[26.25%] lg:w-[24.75%] lg:h-[22.56%]*/}
      {/*  xl:top-[25.03%] xl:left-[26.25%] xl:w-[24.75%] xl:h-[22.56%]">*/}
      {/*  <Image*/}
      {/*    src={`${basePath}/ellipse-layer-13-blue.png`}*/}
      {/*    alt="Ellipse Layer 13 Blue"*/}
      {/*    fill*/}
      {/*    className="object-contain object-top mix-blend-multiply"*/}
      {/*  />*/}
      {/*</div>*/}
      <div className="absolute
        top-[36.25%] left-[calc(50%-10.7%)]
        w-[21.4%] h-[19.49%]
        xs:top-[36.25%] xs:left-[calc(50%-10.7%)] xs:w-[21.4%] xs:h-[19.49%]
        sm:top-[36.25%] sm:left-[calc(50%-10.7%)] sm:w-[21.4%] sm:h-[19.49%]
        md:top-[36.25%] md:left-[calc(50%-10.7%)] md:w-[21.4%] md:h-[19.49%]
        lg:top-[36.25%] lg:left-[calc(50%-10.7%)] lg:w-[21.4%] lg:h-[19.49%]
        xl:top-[36.25%] xl:left-[calc(50%-10.7%)] xl:w-[21.4%] xl:h-[19.49%]">
        <Image
          src={`${basePath}/ellipse-layer-14.png`}
          alt="Ellipse Layer 14"
          fill
          className="object-contain object-top mix-blend-multiply"
        />
      </div>


      {/* Character Elements */}
      {/* Flying Disc */}
      <div className="absolute
        top-[52.58%] left-[calc(50%-29.85%)]
        w-[59.7%] h-[14.34%]
        xs:top-[52.58%] xs:left-[calc(50%-29.85%)] xs:w-[59.7%] xs:h-[14.34%]
        sm:top-[52.58%] sm:left-[calc(50%-29.85%)] sm:w-[59.7%] sm:h-[14.34%]
        md:top-[52.58%] md:left-[calc(50%-29.85%)] md:w-[59.7%] md:h-[14.34%]
        lg:top-[52.58%] lg:left-[calc(50%-29.85%)] lg:w-[59.7%] lg:h-[14.34%]
        xl:top-[52.58%] xl:left-[calc(50%-29.85%)] xl:w-[59.7%] xl:h-[14.34%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/dia-bay.webp'}
          alt="Flying Disc"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>

      {/* Decorative Elements */}
      <div className="absolute
        top-[27%] left-[calc(50%-20.975%)]
        w-[41.95%] h-[11.12%]
        xs:top-[27%] xs:left-[calc(50%-20.975%)] xs:w-[41.95%] xs:h-[11.12%]
        sm:top-[27%] sm:left-[calc(50%-20.975%)] sm:w-[41.95%] sm:h-[11.12%]
        md:top-[27%] md:left-[calc(50%-20.975%)] md:w-[41.95%] md:h-[11.12%]
        lg:top-[27%] lg:left-[calc(50%-20.975%)] lg:w-[41.95%] lg:h-[11.12%]
        xl:top-[27%] xl:left-[calc(50%-20.975%)] xl:w-[41.95%] xl:h-[11.12%]">
        <Image
          src={`${basePath}/hearts.png`}
          alt="Heart"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>
      <div className="absolute
        top-[52.69%] left-[-13.6%]
        w-[51.5%] h-[69.13%]
        xs:top-[52.69%] xs:left-[-13.6%] xs:w-[51.5%] xs:h-[69.13%]
        sm:top-[52.69%] sm:left-[-13.6%] sm:w-[51.5%] sm:h-[69.13%]
        md:top-[52.69%] md:left-[-13.6%] md:w-[51.5%] md:h-[69.13%]
        lg:top-[52.69%] lg:left-[-13.6%] lg:w-[51.5%] lg:h-[69.13%]
        xl:top-[52.69%] xl:left-[-13.6%] xl:w-[51.5%] xl:h-[69.13%]">
        <Image
          src={`${basePath}/soc-trai.png`}
          alt="Sock 1"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>
      <div className="absolute
        top-[52.69%] right-[-13.6%]
        w-[51.5%] h-[69.13%]
        xs:top-[52.69%] xs:right-[-13.6%] xs:w-[51.5%] xs:h-[69.13%]
        sm:top-[52.69%] sm:right-[-13.6%] sm:w-[51.5%] sm:h-[69.13%]
        md:top-[52.69%] md:right-[-13.6%] md:w-[51.5%] md:h-[69.13%]
        lg:top-[52.69%] lg:right-[-13.6%] lg:w-[51.5%] lg:h-[69.13%]
        xl:top-[52.69%] xl:right-[-13.6%] xl:w-[51.5%] xl:h-[69.13%]">
        <Image
          src={`${basePath}/soc-phai.png`}
          alt="Sock 2"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>

      {/* Magical Effects */}
      <div className="absolute
        top-[25%] left-[calc(50%-30.925%)]
        w-[61.85%] h-[27.28%]
        xs:top-[25%] xs:left-[calc(50%-30.925%)] xs:w-[61.85%] xs:h-[27.28%]
        sm:top-[25%] sm:left-[calc(50%-30.925%)] sm:w-[61.85%] sm:h-[27.28%]
        md:top-[25%] md:left-[calc(50%-30.925%)] md:w-[61.85%] md:h-[27.28%]
        lg:top-[25%] lg:left-[calc(50%-30.925%)] lg:w-[61.85%] lg:h-[27.28%]
        xl:top-[25%] xl:left-[calc(50%-30.925%)] xl:w-[61.85%] xl:h-[27.28%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/tinh-van-ao-anh.webp'}
          alt="Magical Sparkles"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>

      {/* Main Logo and Branding */}
      {/* Logo */}
      <div className="absolute
        top-[13%] left-[calc(50%-9.975%)]
        w-[19.95%] h-[15.2%]
        xs:top-[13%] xs:left-[calc(50%-9.975%)] xs:w-[19.95%] xs:h-[15.2%]
        sm:top-[13%] sm:left-[calc(50%-9.975%)] sm:w-[19.95%] sm:h-[15.2%]
        md:top-[13%] md:left-[calc(50%-9.975%)] md:w-[19.95%] md:h-[15.2%]
        lg:top-[13%] lg:left-[calc(50%-9.975%)] lg:w-[19.95%] lg:h-[15.2%]
        xl:top-[13%] xl:left-[calc(50%-9.975%)] xl:w-[19.95%] xl:h-[15.2%]">
        <Image
          src={`${basePath}/vuon-logo-main.png`}
          alt="Vuon Main Logo"
          fill
          className="object-contain object-top "
          priority
        />
      </div>


      {/* Artist Lineup Section */}
      <div className="absolute
        top-[2%] left-[calc(50%-35.675%)]
        w-[71.35%] h-full
        xxs:top-[2%] xxs:left-[calc(50%-35.675%)] xxs:w-[71.35%]
        sm:top-[12%] sm:left-[calc(50%-35.675%)] sm:w-[71.35%]
        md:top-[18%] md:left-[calc(50%-35.675%)] md:w-[71.35%]
        lg:top-[25%] lg:left-[calc(50%-35.675%)] lg:w-[71.35%]
        xl:top-[25%] xl:left-[calc(50%-35.675%)] xl:w-[71.35%]">
        {/*<Image*/}
        {/*  src={`${basePath}/artists.png`}*/}
        {/*  alt="Artist Lineup"*/}
        {/*  fill*/}
        {/*  className="object-contain object-top"*/}
        {/*/>*/}
        <div className="flex items-center justify-center">
          <ArtistLineup />
        </div>
      </div>

      {/* Time and Location Block */}
      <div className="absolute
        top-[60.8%] left-1/2 transform -translate-x-1/2
        w-[41.2%] h-[9.6%]
        xs:top-[60.8%] xs:w-[41.2%] xs:h-[9.6%]
        sm:top-[60.8%] sm:w-[41.2%] sm:h-[9.6%]
        md:top-[60.8%] md:w-[41.2%] md:h-[9.6%]
        lg:top-[60.8%] lg:w-[41.2%] lg:h-[9.6%]
        xl:top-[60.8%] xl:w-[41.2%] xl:h-[9.6%]
        flex items-center justify-center px-4">
        <div className="relative text-white text-center ">
          <TimeBlock location="cung thể thao quần ngựa" time="19:30" day="CHỦ NHẬT" date="05.10.2025" />
        </div>
      </div>
      <div className="absolute
        top-[75.43%] left-[calc(50%-13.975%)]
        w-[27.95%] h-[2.04%]
        xs:top-[75.43%] xs:left-[calc(50%-13.975%)] xs:w-[27.95%] xs:h-[2.04%]
        sm:top-[75.43%] sm:left-[calc(50%-13.975%)] sm:w-[27.95%] sm:h-[2.04%]
        md:top-[75.43%] md:left-[calc(50%-13.975%)] md:w-[27.95%] md:h-[2.04%]
        lg:top-[75.43%] lg:left-[calc(50%-13.975%)] lg:w-[27.95%] lg:h-[2.04%]
        xl:top-[75.43%] xl:left-[calc(50%-13.975%)] xl:w-[27.95%] xl:h-[2.04%]">
        <Image
          src={`${basePath}/loe-sang-main.png`}
          alt="Main Bright Light"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>

      {/* Đối tác đồng hành */}
      <div className="absolute
        top-[78.8%] left-1/2 transform -translate-x-1/2
        w-[29.25%] h-[18.9%]
        xs:top-[78.8%] xs:w-[29.25%] xs:h-[18.9%]
        sm:top-[78.8%] sm:w-[29.25%] sm:h-[18.9%]
        md:top-[78.8%] md:w-[29.25%] md:h-[18.9%]
        lg:top-[78.8%] lg:w-[29.25%] lg:h-[18.9%]
        xl:top-[78.8%] xl:w-[29.25%] xl:h-[18.9%]">
        <Image
          src={`${basePath}/doi-tac-dong-hanh.png`}
          alt="Đối tác đồng hành"
          fill
          className="object-contain object-top"
        />
      </div>

      {/* Main Action Button Block */}
      <div className="absolute
        top-[81.2%] left-1/2 transform -translate-x-1/2
        w-full h-[18.9%]
        xs:top-[81.2%] xs:h-[18.9%]
        sm:top-[81.2%] sm:h-[18.9%]
        md:top-[81.2%] md:h-[18.9%]
        lg:top-[81.2%] lg:h-[18.9%]
        xl:top-[81.2%] xl:h-[18.9%]
        flex items-center justify-center px-4">

        <div className={'px-1 w-full mx-auto'}>
          <CtaButton
            text={
              <>
                <p className={'uppercase'} style={{
                  fontSize: '16px',
                }}>{'ĐĂNG KÝ NHẬN VÉ'}</p>
                <p className="text-[16px]">(Miễn phí)</p>
              </>
            }
            className={'mx-auto w-[20%] min-w-[291px] max-w-[665px]'}
            contentClassName="font-next_roboto font-bold text-[16px] lg:text-[36px]"
            // onClick={scrollToRegisterSection}
          />
        </div>
      </div>

      {/*<div className="absolute*/}
      {/*  top-[81.8%] left-[-17.05%]*/}
      {/*  w-[51.1%] h-[43.6%]*/}
      {/*  xxs:top-[81.8%] xxs:left-[-17.05%] xxs:w-[51.1%] xxs:h-[43.6%]*/}
      {/*  xs:top-[81.8%] xs:left-[-17.05%] xs:w-[51.1%] xs:h-[43.6%]*/}
      {/*  sm:top-[81.8%] sm:left-[-17.05%] sm:w-[51.1%] sm:h-[43.6%]*/}
      {/*  md:top-[81.8%] md:left-[-17.05%] md:w-[51.1%] md:h-[43.6%]*/}
      {/*  lg:top-[81.8%] lg:left-[-17.05%] lg:w-[51.1%] lg:h-[43.6%]*/}
      {/*  xl:top-[81.8%] xl:left-[-17.05%] xl:w-[51.1%] xl:h-[43.6%]">*/}
      {/*  <Image*/}
      {/*    src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/ellipse-layer-12-pink.webp'}*/}
      {/*    alt="Ellipse Layer 12 Pink"*/}
      {/*    fill*/}
      {/*    className="object-contain object-top mix-blend-screen"*/}
      {/*  />*/}
      {/*</div>*/}
      <div className="absolute
        top-[86.3%] left-[11%]
        w-[51.1%] h-[43.6%]
        xxs:top-[86.3%] xxs:left-[11%] xxs:w-[51.1%] xxs:h-[43.6%]
        xs:top-[86.3%] xs:left-[11%] xs:w-[51.1%] xs:h-[43.6%]
        sm:top-[86.3%] sm:left-[11%] sm:w-[51.1%] sm:h-[43.6%]
        md:top-[86.3%] md:left-[11%] md:w-[51.1%] md:h-[43.6%]
        lg:top-[86.3%] lg:left-[11%] lg:w-[51.1%] lg:h-[43.6%]
        xl:top-[86.3%] xl:left-[11%] xl:w-[51.1%] xl:h-[43.6%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/ellipse-layer-19-det.webp'}
          alt="Ellipse Layer 19 Detail"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>
      <div className="absolute
        top-[41.9%] left-[82.9%]
        w-[45.35%] h-[41.35%]
        xxs:top-[41.9%] xxs:left-[82.9%] xxs:w-[45.35%] xxs:h-[41.35%]
        xs:top-[41.9%] xs:left-[82.9%] xs:w-[45.35%] xs:h-[41.35%]
        sm:top-[41.9%] sm:left-[82.9%] sm:w-[45.35%] sm:h-[41.35%]
        md:top-[41.9%] md:left-[82.9%] md:w-[45.35%] md:h-[41.35%]
        lg:top-[41.9%] lg:left-[82.9%] lg:w-[45.35%] lg:h-[41.35%]
        xl:top-[41.9%] xl:left-[82.9%] xl:w-[45.35%] xl:h-[41.35%]">
        <Image
          src={'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/ellipse-layer-18-pink.webp'}
          alt="Ellipse Layer 18 Pink"
          fill
          className="object-contain object-top mix-blend-screen"
        />
      </div>

      <div className="absolute
        top-[-19.5%] left-[62.45%]
        w-[51.1%] h-[43.6%]
        xxs:top-[-19.5%] xxs:left-[62.45%] xxs:w-[51.1%] xxs:h-[43.6%]
        xs:top-[-19.5%] xs:left-[62.45%] xs:w-[51.1%] xs:h-[43.6%]
        sm:top-[-19.5%] sm:left-[62.45%] sm:w-[51.1%] sm:h-[43.6%]
        md:top-[-19.5%] md:left-[62.45%] md:w-[51.1%] md:h-[43.6%]
        lg:top-[-19.5%] lg:left-[62.45%] lg:w-[51.1%] lg:h-[43.6%]
        xl:top-[-19.5%] xl:left-[62.45%] xl:w-[51.1%] xl:h-[43.6%]">
        <Image
          src={`${basePath}/ellipse-layer-20-det-pink.png`}
          alt="Ellipse Layer 20 Detail"
          fill
          className="object-contain object-top mix-blend-lighten"
        />
      </div>

    </div>
  );
};

export default SuperHeroBanner;
